import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, Copy, Loader2, AlertTriangle } from "lucide-react";
import { useApp } from "@/App";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import QRCode from "react-qr-code";
import { getCryptoIconUrl, getCryptoIconFallback } from "@/lib/cryptoIcons";

// Пополнение по счёту (эквайринг Rapira).
//
// Отличие от старого экрана принципиальное, и оно определяет разметку: адрес
// здесь ОДНОРАЗОВЫЙ и ждёт ровно один перевод. Второй перевод на тот же адрес
// придёт на погашенный адрес, и его придётся возвращать через поддержку.
// Поэтому сумма задаётся заранее и стоит на экране крупно, а предупреждение
// про одну транзакцию живёт рядом с адресом, а не спрятано в подсказке.

const POLL_INTERVAL_MS = 5000;
const PRESETS = [50, 100, 200, 500];

// Монета сети — для значка. Токен всюду USDT, различаются только сети,
// поэтому значок сети идёт бейджем поверх значка USDT.
const CHAIN_COIN = {
  TRX_USDT: "TRX",
  BSC_USDT: "BNB",
  ETH_USDT: "ETH",
};

const CoinBadge = ({ tokenId, size = 40 }) => {
  const chain = CHAIN_COIN[tokenId] || "USDT";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <img
        src={getCryptoIconUrl("USDT")}
        alt="USDT"
        className="w-full h-full rounded-full"
        loading="lazy"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = getCryptoIconFallback("USDT");
        }}
      />
      <img
        src={getCryptoIconUrl(chain)}
        alt={chain}
        className="absolute -bottom-0.5 -right-0.5 w-[45%] h-[45%] rounded-full ring-2 ring-background"
        loading="lazy"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = getCryptoIconFallback(chain);
        }}
      />
    </div>
  );
};

const ProcessingTopUpPage = () => {
  const navigate = useNavigate();
  const { api, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [networks, setNetworks] = useState([]);
  const [feeAmount, setFeeAmount] = useState(0);
  const [tokenId, setTokenId] = useState(null);
  const [amount, setAmount] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const selected = useMemo(
    () => networks.find((n) => n.tokenId === tokenId) || null,
    [networks, tokenId]
  );
  const minAmount = selected ? Number(selected.minAmount) : 0;
  const numericAmount = Number(String(amount).replace(",", "."));
  const amountValid = Number.isFinite(numericAmount) && numericAmount > 0;
  const belowMinimum = amountValid && !!selected && numericAmount < minAmount;
  const canSubmit = !!selected && amountValid && !belowMinimum && !loading;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/processing/networks");
        if (!alive) return;
        const rows = Array.isArray(res?.data?.networks) ? res.data.networks : [];
        setNetworks(rows);
        setFeeAmount(Number(res?.data?.feeAmount || 0));
        if (rows.length) setTokenId(rows[0].tokenId);
      } catch (err) {
        // Экран открыт тем, кому он не положен, или контур выключен — уводим
        // молча: объяснять человеку про пилот незачем.
        navigate("/", { replace: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, navigate]);

  // Возврат на главную, а не на /topup: тот экран сам перебрасывает сюда, и
  // «назад» превратился бы в петлю.
  const goBack = useCallback(() => navigate("/"), [navigate]);

  useEffect(() => {
    if (isTelegramWebApp) setBackHandler?.(goBack);
    return () => clearBackHandler?.();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, goBack]);

  // Пока счёт открыт, спрашиваем его состояние. Зачисление в любом случае
  // сделает бэкенд — по уведомлению провайдера или своим поллером; этот опрос
  // нужен только экрану, чтобы человек увидел результат, не перезаходя.
  useEffect(() => {
    if (!invoice?.invoiceId || invoice.credited) return undefined;
    const tick = async () => {
      try {
        const res = await api.get(`/processing/invoice/${invoice.invoiceId}`);
        const fresh = res?.data;
        if (!fresh) return;
        setInvoice(fresh);
        if (fresh.credited) {
          haptic?.("success");
        }
      } catch (_) {
        // Молча: следующий тик повторит.
      }
    };
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [api, haptic, invoice?.invoiceId, invoice?.credited]);

  const createInvoice = async () => {
    if (!canSubmit) return;
    haptic?.("medium");
    setLoading(true);
    try {
      const res = await api.post("/processing/invoice", {
        tokenId,
        amount: numericAmount,
      });
      setInvoice(res?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось выставить счёт");
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = async () => {
    if (!invoice?.address) return;
    try {
      await navigator.clipboard.writeText(invoice.address);
      setCopied(true);
      haptic?.("light");
      toast.success("Адрес скопирован");
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error("Не удалось скопировать");
    }
  };

  const headerTitle = invoice
    ? invoice.credited
      ? "Пополнение"
      : "Оплата счёта"
    : "Пополнение по счёту";

  return (
    // Каркас как у остальных экранов: фиксированная высота, липкая шапка с
    // отступом от чёлки (--lp-top внутри .lp-topbar) и прокрутка в теле. Без
    // .lp-topbar содержимое уезжает под системную панель Telegram.
    <div
      className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col"
      data-testid="processing-topup-page"
    >
      <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
        {showInlineBack ? (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex h-8 w-8 items-center justify-center text-foreground"
            aria-label="Назад"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold text-foreground">{headerTitle}</h1>
        <span aria-hidden="true" className="h-8 w-8" />
      </div>

      <div
        className="flex-1 overflow-y-auto overscroll-contain px-4 pt-5 pb-6"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* --- шаг 1: сумма и сеть --- */}
        {!invoice && (
          <div className="flex flex-col gap-6 lp-rise lp-rise-1">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Сумма пополнения</label>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className={cn(
                    "w-full bg-gray-50 dark:bg-gray-800 rounded-2xl p-5 pr-20 text-2xl font-semibold text-foreground outline-none border transition-colors",
                    belowMinimum ? "border-red-400" : "border-transparent focus:border-primary/40"
                  )}
                  data-testid="processing-amount"
                />
                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                  USDT
                </span>
              </div>

              <div className="flex gap-2 flex-wrap mt-3">
                {PRESETS.filter((p) => !minAmount || p >= minAmount).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      haptic?.("light");
                      setAmount(String(p));
                    }}
                    className={cn(
                      "px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-colors",
                      Number(amount) === p
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))] text-muted-foreground"
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>

              {belowMinimum ? (
                <p className="text-xs mt-2 text-red-500">
                  Минимум для этой сети — {minAmount} USDT
                </p>
              ) : amountValid && feeAmount > 0 ? (
                // Обе величины до перевода, а не удержание постфактум.
                <div className="mt-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 divide-y divide-gray-200/70 dark:divide-gray-700/50">
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-muted-foreground">Комиссия</span>
                    <span className="text-sm font-medium text-foreground">
                      {feeAmount.toFixed(2)} USDT
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-muted-foreground">Зачислим на баланс</span>
                    <span
                      className="text-sm font-semibold text-green-500"
                      data-testid="processing-credit-preview"
                    >
                      {Math.max(numericAmount - feeAmount, 0).toFixed(2)} USDT
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-xs mt-2 min-h-[1rem] text-gray-400">
                  {selected ? `Минимум ${minAmount} USDT` : ""}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Сеть перевода</label>
              <div className="flex flex-col gap-2" data-testid="processing-networks">
                {networks.map((n) => {
                  const active = tokenId === n.tokenId;
                  return (
                    <button
                      key={n.tokenId}
                      type="button"
                      onClick={() => {
                        haptic?.("light");
                        setTokenId(n.tokenId);
                      }}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors",
                        active
                          ? "border-primary bg-primary/5"
                          : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))]"
                      )}
                    >
                      <CoinBadge tokenId={n.tokenId} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[15px] truncate">{n.label}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          от {n.minAmount} USDT
                        </div>
                      </div>
                      <span
                        className={cn(
                          "w-5 h-5 rounded-full border flex items-center justify-center shrink-0",
                          active
                            ? "border-primary bg-primary"
                            : "border-gray-300 dark:border-gray-600"
                        )}
                      >
                        {active && <Check className="w-3 h-3 text-primary-foreground" />}
                      </span>
                    </button>
                  );
                })}
                {networks.length === 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Загружаем сети
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* --- шаг 2: реквизиты --- */}
        {invoice && !invoice.credited && (
          <div className="flex flex-col gap-4 lp-rise lp-rise-1">
            <div className="rounded-2xl bg-gray-50 dark:bg-gray-800/60 px-4 py-5 flex flex-col items-center gap-1">
              <span className="text-sm text-muted-foreground">Переведите ровно</span>
              <span
                className="text-3xl font-bold text-foreground tracking-tight"
                data-testid="processing-amount-due"
              >
                {Number(invoice.amount).toFixed(2)} {invoice.coin}
              </span>
              {invoice.feeAmount > 0 && (
                <span className="text-xs text-muted-foreground">
                  на баланс ляжет {Number(invoice.creditAmount).toFixed(2)} {invoice.coin} ·
                  комиссия {Number(invoice.feeAmount).toFixed(2)}
                </span>
              )}
              <span className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background border border-[rgb(var(--lp-edge)/var(--lp-edge-a))]">
                <CoinBadge tokenId={invoice.tokenId} size={18} />
                <span className="text-xs font-medium">{invoice.network}</span>
              </span>
            </div>

            <div className="flex flex-col items-center gap-4 rounded-2xl border border-[rgb(var(--lp-edge)/var(--lp-edge-a))] px-4 py-5">
              <div className="bg-white p-3 rounded-2xl">
                <QRCode
                  value={invoice.address || ""}
                  size={188}
                  level="H"
                  data-testid="processing-qr"
                />
              </div>

              <button
                type="button"
                onClick={copyAddress}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-800 text-left active:opacity-70 transition-opacity"
                aria-label="Скопировать адрес"
              >
                <span
                  className="flex-1 text-[13px] leading-snug break-all font-mono text-foreground"
                  data-testid="processing-address"
                >
                  {invoice.address}
                </span>
                {copied ? (
                  <Check className="w-5 h-5 text-green-500 shrink-0" />
                ) : (
                  <Copy className="w-5 h-5 text-muted-foreground shrink-0" />
                )}
              </button>
            </div>

            {/* Предупреждение стоит под адресом, но до статуса: второй перевод
                на этот адрес возвращается только через поддержку. */}
            <div className="flex gap-2.5 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/25">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-200">
                Адрес одноразовый и ждёт один перевод. Отправьте всю сумму{" "}
                <b>одной транзакцией</b> — повторный перевод на этот адрес придётся возвращать
                через поддержку.
              </p>
            </div>

            <div className="flex items-center justify-center gap-2.5 py-2 text-sm text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              Ждём поступление — баланс обновится сам
            </div>
          </div>
        )}

        {/* --- шаг 3: зачислено --- */}
        {invoice?.credited && (
          <div
            className="flex flex-col items-center gap-3 pt-16 lp-rise lp-rise-1"
            data-testid="processing-done"
          >
            <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-2xl font-bold tracking-tight">
              +{Number(invoice.creditAmount || invoice.paidAmount || invoice.amount).toFixed(2)}{" "}
              {invoice.coin}
            </p>
            <p className="text-sm text-muted-foreground">Зачислено на баланс</p>
          </div>
        )}
      </div>

      {/* Кнопка живёт в нижней панели, а не в потоке: на длинном экране она
          иначе уезжает под сгиб, и до неё надо доскроллить. */}
      <div className="lp-bar sticky bottom-0 px-4 pt-3 pb-[calc(0.75rem+var(--lp-bottom))] border-t border-[var(--lp-hair)]">
        {!invoice && (
          <button
            onClick={createInvoice}
            disabled={!canSubmit}
            className={cn(
              "w-full py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2",
              canSubmit
                ? "bg-primary text-primary-foreground"
                : "bg-gray-200 dark:bg-gray-800 text-gray-400"
            )}
            data-testid="processing-create"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Получить реквизиты
          </button>
        )}

        {invoice && !invoice.credited && (
          <button
            onClick={goBack}
            className="w-full py-3.5 rounded-xl font-semibold bg-gray-100 dark:bg-gray-800 text-foreground"
          >
            Оплачу позже
          </button>
        )}

        {invoice?.credited && (
          <button
            onClick={() => navigate("/")}
            className="w-full py-3.5 rounded-xl font-semibold bg-primary text-primary-foreground"
          >
            Готово
          </button>
        )}
      </div>
    </div>
  );
};

export default ProcessingTopUpPage;
